import { AnimatePresence, motion } from 'framer-motion';
import { useContext, useEffect, useMemo } from 'react';
import { KlartextEventConfigContext } from '../providers/klartextEventConfig';
import { useKpiCollection } from '../hooks/useKpiCollection';
import ListeningView from './ListeningView';
import { VerbiagesKpiPoint } from '@configTypes/kpiVerbiages';
import { useGlobalState } from '../providers/globalState';
import { KlartextEventConfig } from '@common/types/klartextEventConfig';
import { FrontendSpecificConfig } from '@configTypes/eventConfigs';

type SpeakerVerbiage = {
  index: number;
  speaker: string;
  verbiage: string;
  explanation: string;
};

export const VerbiagesView = () => {
  const eventConfig = useContext(KlartextEventConfigContext);
  const [verbiageKpis] = useKpiCollection<VerbiagesKpiPoint[]>({
    eventId: eventConfig?.eventId ?? null,
    kpiName: 'Verbiages',
    initialState: [] as VerbiagesKpiPoint[],
  });

  const globalState = useGlobalState();
  const { dispatch } = globalState ?? {};

  const verbiages = useMemo(() => {
    const candidateIndices: { [speaker: string]: number } = {};

    return verbiageKpis
      .reduce<SpeakerVerbiage[]>((acc, kpi) => {
        const newVerbiages = kpi.value
          .filter(
            verbiage =>
              verbiage.speaker === eventConfig?.frontendSpecificConfig?.participants.democrat.name ||
              verbiage.speaker === eventConfig?.frontendSpecificConfig?.participants.republican.name,
          )
          .map(verbiage => {
            if (!candidateIndices[verbiage.speaker]) {
              candidateIndices[verbiage.speaker] = 0;
            }
            candidateIndices[verbiage.speaker]++;

            return {
              index: candidateIndices[verbiage.speaker],
              speaker: verbiage.speaker,
              verbiage: verbiage.platitude,
              explanation: verbiage.explanation,
            };
          });

        // remove duplicate verbiages
        const uniqueVerbiages = newVerbiages.filter(
          (verbiage, index, self) => index === self.findIndex(t => t.verbiage === verbiage.verbiage),
        );
        return [...acc, ...uniqueVerbiages];
      }, [])
      .reverse();
  }, [verbiageKpis]);

  useEffect(() => {
    dispatch?.({ type: 'SET_VERBIAGE_COUNT', payload: verbiages.length });
  }, [verbiages]);

  if (verbiages.length === 0 || !eventConfig) {
    return <ListeningView />;
  }

  return (
    <div className="space-y-8">
      <AnimatePresence>
        {verbiages.map((verbiage, i) => (
          <motion.div
            layout
            key={`verbiage-${verbiage.speaker}-${verbiage.index}`}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.5 }}>
            <Verbiage
              speaker={verbiage.speaker}
              eventConfig={eventConfig}
              verbiage={verbiage.verbiage}
              description={verbiage.explanation}
              index={verbiage.index}
            />
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
};

const Verbiage = ({
  speaker,
  verbiage: phrase,
  eventConfig,
  description: explanation,
  index,
}: {
  speaker: string;
  eventConfig: KlartextEventConfig<FrontendSpecificConfig>;
  verbiage: string;
  description: string;
  index: number;
}) => {
  return (
    <div className={`mt-4 flex flex-col gap-2`}>
      <div className="flex flex-row items-center">
        <div
          className={`flex aspect-square h-8 items-center justify-center rounded-full border-[0.1875rem] border-eidraWhite ${
            speaker === eventConfig?.frontendSpecificConfig?.participants.republican.name
              ? 'bg-republican'
              : 'bg-democrat'
          } text-eidraWhite`}>
          <span className="text-center font-medium">{index}</span>
        </div>
        <div className="relative flex flex-auto items-center bg-eidraWhite px-3 py-1 before:absolute before:-left-4 before:bottom-0 before:top-0 before:-z-10 before:block before:w-4 before:bg-eidraWhite">
          <span className="text-[0.625rem] font-medium leading-none tracking-widest">
            {speaker.toUpperCase()} PLATITUDE
          </span>
        </div>
      </div>
      <div className="space-y-2 first-letter:uppercase">
        <strong className="text-[1.875rem] leading-none text-eidraBlack">{phrase}</strong>
        <p className="text-[1.125rem] leading-[1.3] text-eidraBlack">{explanation}</p>
      </div>
    </div>
  );
};
